import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  callIdSelector,
  callLanguageCodeSelector,
  sipCallIdSelector,
} from 'features/call/call-base/store/selectors';
import { CallDaoService } from 'features/call/call-base/services';
import { handleError } from 'features/notification/store';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { deafPhoneNumberSelector } from 'features/call/call-deaf/store';
import { primaryHearingPhoneNumberSelector } from 'features/call/call-hearing/store';
import { AppConfigService } from 'features/app-config/services';
import type { RootState } from 'features/app/store/store';
import {
  finishRequiredApiCallsPending,
  requiredApiCallsPending,
} from 'features/call/call-base/store';

/**
 * Thunk to add a missed call notification
 * Will only add notification if in VRS mode
 */
export const missedCallAddNotificationThunk = createAsyncThunk(
  'vrsCall/missedCallAddNotification',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;

    // Check if in VRS mode
    const isVrs = isVrsModeSelector(state);
    if (!isVrs) {
      return;
    }

    try {
      dispatch(requiredApiCallsPending());
      const callId = callIdSelector(state);
      const sipCallId = sipCallIdSelector(state);
      const deafPhoneNumber = deafPhoneNumberSelector(state);
      const primaryHearingNumber = primaryHearingPhoneNumberSelector(state);
      const language = callLanguageCodeSelector(state);

      const defaultOutboundCallerIdEnglish =
        AppConfigService.defaultOutboundCallerIdEnglish;
      const defaultOutboundCallerIdSpanish =
        AppConfigService.defaultOutboundCallerIdSpanish;

      const calleeNumber = deafPhoneNumber;
      const callerNumber =
        primaryHearingNumber ||
        (language === 'es'
          ? defaultOutboundCallerIdSpanish
          : defaultOutboundCallerIdEnglish);

      await CallDaoService.missedCallAddNotification(
        callId,
        sipCallId,
        calleeNumber,
        callerNumber as string
      );

      dispatch(
        sendAnalyticsInfo({
          Method: 'missedCallAddNotification',
          message: 'missed call add notification successful',
        })
      );
      dispatch(finishRequiredApiCallsPending());

      return true;
    } catch (error) {
      dispatch(finishRequiredApiCallsPending());
      dispatch(
        handleError({
          error,
          message: 'handle missed call add notification failed',
          methodName: 'missedCallAddNotification',
          disableUserNotification: true,
        })
      );
      console.error('DEBUG: missed call add notification failed', error);
      return rejectWithValue(error);
    }
  }
);
